import { Component, OnInit, OnDestroy, HostListener, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InvoiceV2Service } from '../../../services/invoice-v2.service';
import { HttpHelpService } from '../../../Data/service/http-help.service';
import Swal from 'sweetalert2';
import { InvoiceStatusCode, MultiLangMessagePayload, StoredPaymentStatus, UpdateInvoicePaymentBody } from '../../../models/invoice-v2.model';
import {
  computeProcedureActionFlags,
  canRequestAllocationForType,
  getProcedureLabel,
  isProcedureCycleConfirmResponse,
  ProcedureActionFlags,
  normalizeProcedureInvoiceRow,
} from '../invoice-v2-procedure.util';
import {
  applyPaymentFieldsToInvoiceRow,
  buildClearDueDatePatchBody,
  buildPaymentStatusPatchBody,
  buildUnpaidWithDueDatePatchBody,
  computeEffectivePaymentStatus,
  formatDueDateForApi,
  getEffectivePaymentStatusBadgeClass,
  getInvoicePaymentStatusLabel,
  isPaymentTrackingInvoiceType,
  needsDueDateBeforeUnpaid,
  parseDueDateFromApi,
  shouldShowPaymentTrackingInUi,
  shouldSkipPaymentStatusPatch,
} from '../invoice-v2-payment.util';
import {
  isForeignDocument,
  resolveDocumentCurrency,
  resolveForeignCurrencyFromInvoice,
} from '../invoice-v2-currency.util';

/** Popup lists orders (100), missions (200), financial requests (332); merge API requires same type as current document. */
const BULK_MERGE_POPUP_INVOICE_TYPES = [100, 200, 332] as const;

@Component({
  selector: 'app-invoices-v2-view',
  templateUrl: './invoices-v2-view.component.html',
  styleUrls: ['./invoices-v2-view.component.scss'],
})
export class InvoicesV2ViewComponent implements OnInit, OnDestroy {
  curentLang: string = '';
  invoiceId: number;
  private queryParamsSub?: Subscription;
  invoice: any = null;
  invoiceHtml: SafeHtml = '';
  invoiceHtmlUrl: SafeResourceUrl | null = null;
  private invoiceHtmlBlobUrl: string | null = null;
  isLoading: boolean = false;

  // Tax authority actions
  canCancel: boolean = false;
  canFurtherObjection: boolean = false;
  canAfterHearingApproval: boolean = false;
  canContinue: boolean = false;
  canResendSecond: boolean = false;
  procedureFlags: ProcedureActionFlags = computeProcedureActionFlags(null);
  showProcedureModal: boolean = false;
  /** Invoice confirmed but created without allocation (allocation_number=0) – show option to request allocation number */
  canRequestAllocationNumber: boolean = false;
  /** Invoice type supports bulk-convert → show Merge button */
  canBulkMerge: boolean = false;

  // Bulk merge modal
  showBulkMergeModal: boolean = false;
  bulkMergeInvoicesList: any[] = [];
  bulkMergeSelectedIds: number[] = [];
  isLoadingBulkMerge: boolean = false;
  bulkMergeExcludingId: number | null = null;

  // Invoice preview modal (inside bulk merge)
  showInvoicePreviewModal: boolean = false;
  previewInvoiceId: number | null = null;
  previewInvoiceNumber: string = '';
  previewInvoiceUrl: SafeResourceUrl | null = null;
  private previewBlobUrl: string | null = null;
  isLoadingPreview: boolean = false;

  invoiceTypes: any[] = [];

  // Invoice list for selection
  showInvoiceList: boolean = false;
  invoicesList: any[] = [];
  selectedInvoiceIds: number[] = [];
  isLoadingList: boolean = false;

  // Email dialog
  showEmailDialog: boolean = false;
  emailForm: FormGroup;

  /** Inline payment editing on invoice details */
  showPaymentStatusMenu = false;
  paymentStatusMenuStyle: { top: string; left: string } | null = null;
  private paymentStatusAnchorEl: HTMLElement | null = null;
  showDueDateEditor = false;
  dueDatePopoverStyle: { top: string; left: string; maxHeight?: string } | null = null;
  private dueDateAnchorEl: HTMLElement | null = null;
  @ViewChild('dueDatePopover') dueDatePopoverRef?: ElementRef<HTMLElement>;
  private static readonly DUE_DATE_POPOVER_WIDTH = 300;
  private static readonly DUE_DATE_POPOVER_ESTIMATED_HEIGHT = 420;
  private static readonly PAYMENT_STATUS_MENU_WIDTH = 160;
  private static readonly PAYMENT_STATUS_MENU_HEIGHT = 92;
  dueDateEditValue: Date | null = null;
  /** User chose غير مدفوعة but must pick a due date before PATCH. */
  awaitingDueDateForUnpaid = false;
  isSavingPaymentUpdate = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private invoiceV2Service: InvoiceV2Service,
    private httpService: HttpHelpService,
    private spinner: NgxSpinnerService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    private fb: FormBuilder,
  ) {
    this.curentLang = localStorage.getItem('curentLang') || 'en';
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      savePermanently: [false],
    });
  }

  /**
   * Get the error message from API error response.
   * Prefer the localized field for current language (messageAr/messageHe/messageEn) so the
   * correct text is shown; fall back to message when localized is missing.
   */
  private getApiErrorMessage(error: any, fallback: string): string {
    const e = error?.error;
    if (!e) return fallback;
    if (Array.isArray(e.message)) return e.message.join(' | ');
    const nestedObj =
      e.message != null &&
      typeof e.message === 'object' &&
      !Array.isArray(e.message)
        ? e.message
        : null;
    const src = nestedObj ?? e;
    const msgAr = src.messageAr ?? src.messageAR;
    const msgEn = src.messageEn ?? src.messageEN;
    const msgHe = src.messageHe ?? src.messageHE;
    if (this.curentLang === 'ar' && msgAr) return String(msgAr);
    if (this.curentLang === 'he' && msgHe) return String(msgHe);
    if (msgEn) return String(msgEn);
    if (msgAr) return String(msgAr);
    if (msgHe) return String(msgHe);
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
    return fallback;
  }

  private getApiSuccessMessage(response: any, fallback: string): string {
    if (!response) return fallback;
    const msgAr = response.messageAr ?? response.messageAR;
    const msgEn = response.messageEn ?? response.messageEN;
    const msgHe = response.messageHe ?? response.messageHE;
    const dataMsg = response.data?.message && typeof response.data.message === 'string'
      ? response.data.message.trim()
      : null;

    let base = '';
    if (this.curentLang === 'ar' && msgAr) base = msgAr;
    else if (this.curentLang === 'he' && msgHe) base = msgHe;
    else if (msgEn) base = msgEn;
    else if (response.message && typeof response.message === 'string') base = response.message;
    else base = msgAr || msgHe || fallback;

    return dataMsg ? `${base} — ${dataMsg}` : base;
  }

  ngOnInit(): void {
    this.loadInvoiceTypes();

    // Always listen to queryParams so navigating from history.state-only entry to
    // ?invoiceId= after bulk-merge (same component instance) still reloads the invoice.
    this.queryParamsSub = this.route.queryParams.subscribe((params) => {
      const id = this.resolveViewInvoiceId(params);
      if (id == null) {
        this.router.navigate(['/dashboard/invoices-v2']);
        return;
      }
      if (this.invoiceId !== id) {
        this.invoiceId = id;
        this.loadInvoice();
      }
    });
  }

  /** Document language for PDF/HTML — never the UI language. */
  private getDocumentLang(source?: { document_language?: string } | null): string {
    const lang = source?.document_language || this.invoice?.document_language;
    return lang === 'ar' || lang === 'he' || lang === 'en' ? lang : this.curentLang || 'en';
  }

  /**
   * Prefer ?invoiceId= when present (shareable URL + post-merge navigation);
   * otherwise use Router history state (list used state-only before this fix).
   */
  private resolveViewInvoiceId(params: Params): number | null {
    const raw = params['invoiceId'];
    const fromQuery = raw != null && raw !== '' ? parseInt(String(raw), 10) : NaN;
    if (!isNaN(fromQuery) && fromQuery > 0) {
      return fromQuery;
    }
    const st = (history.state as { invoiceId?: number } | undefined)?.invoiceId;
    if (st != null && !isNaN(+st) && +st > 0) {
      return +st;
    }
    return null;
  }

  /**
   * Load invoice types from API
   */
  private loadInvoiceTypes(): void {
    this.httpService.GetInvoiceTypeList().subscribe({
      next: (response: any) => {
        let typesData = response;
        if (response && response.data) {
          typesData = response.data;
        }
        this.invoiceTypes = typesData
          .filter((t: any) => t.status === 1)
          .map((t: any) => ({
            id: String(t.invoice_type_code),
            name_ar: t.invoice_type_name_ar,
            name_en: t.invoice_type_name_en,
            name_he: t.invoice_type_name_he,
            can_convert: t.can_convert === true || t.can_convert === 1,
          }));
        // Re-evaluate actions after types load (invoice may have loaded first)
        if (this.invoice) {
          this.checkTaxAuthorityActions();
        }
      },
      error: (err) => {
        console.error('Error loading invoice types:', err);
      },
    });
  }

  /**
   * Pre-generate PDF in background only for confirmed invoices.
   * Skipped for drafts to avoid triggering the HTTP interceptor's authority-connection
   * banner on page load before the user performs any action.
   */
  private preGeneratePdf(): void {
    if (!this.invoice || this.invoice.confirm !== 1) {
      return;
    }
    this.invoiceV2Service.generatePdf(this.invoiceId, this.getDocumentLang()).subscribe({
      next: () => {
        },
      error: () => {
        }
    });
  }

  /**
   * Load invoice details.
   * @param redirectOnError When false (e.g. after a successful confirm), a reload failure
   *   just shows the error in place without navigating away — the confirm already succeeded.
   */
  loadInvoice(redirectOnError = true): void {
    this.isLoading = true;
    this.spinner.show();
    this.invoiceV2Service.getInvoiceById(this.invoiceId).subscribe({
      next: (response) => {
        this.invoice = normalizeProcedureInvoiceRow(
          (response?.data != null ? response.data : response) as Record<string, unknown>,
        );
        if (!this.invoice || typeof this.invoice !== 'object') {
          this.isLoading = false;
          this.spinner.hide();
          this.toastr.error(this.curentLang === 'ar' ? 'لم يتم العثور على الفاتورة' : this.curentLang === 'he' ? 'החשבונית לא נמצאה' : 'Invoice not found');
          if (redirectOnError) {
            this.router.navigate(['/dashboard/invoices-v2']);
          }
          return;
        }
        this.checkTaxAuthorityActions();
        this.isLoading = false;
        this.spinner.hide();
        if ((history.state as { openProcedureModal?: boolean } | undefined)?.openProcedureModal) {
          if (this.procedureFlags.showProcedureEntry) {
            this.showProcedureModal = true;
          }
          const st = { ...history.state };
          delete st.openProcedureModal;
          history.replaceState(st, '');
        }
        // Preview + PDF after invoice is loaded so document_language is known
        this.loadInvoiceHtml();
        this.preGeneratePdf();
      },
      error: (error) => {
        this.isLoading = false;
        this.spinner.hide();
        this.toastr.error(this.getApiErrorMessage(error, 'Error loading invoice'));
        if (redirectOnError) {
          this.router.navigate(['/dashboard/invoices-v2']);
        }
      },
    });
  }

  /**
   * Load invoice HTML
   */
  loadInvoiceHtml(): void {
    const docLang = this.getDocumentLang();
    this.invoiceV2Service.getInvoiceHtml(this.invoiceId, docLang).subscribe({
      next: (html) => {
        this.invoiceHtml = this.sanitizer.bypassSecurityTrustHtml(html);
        // Create blob URL for iframe
        this.createInvoiceHtmlUrl(html, docLang);
      },
      error: (error) => {
        console.error('Error loading invoice HTML:', error);
      },
    });
  }

  /**
   * Create blob URL from HTML content for iframe
   */
  private createInvoiceHtmlUrl(html: string, docLang = this.getDocumentLang()): void {
    // Clean up previous URL if exists
    if (this.invoiceHtmlBlobUrl) {
      URL.revokeObjectURL(this.invoiceHtmlBlobUrl);
      this.invoiceHtmlBlobUrl = null;
    }

    // Create a complete HTML document with proper styling
    const fullHtml = `
      <!DOCTYPE html>
      <html lang="${docLang}" dir="${docLang === 'ar' || docLang === 'he' ? 'rtl' : 'ltr'}">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            body {
              margin: 0;
              padding: 20px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              background-color: #ffffff;
              color: #1a1a1a;
            }
            * {
              box-sizing: border-box;
            }
          </style>
        </head>
        <body>
          ${html}
        </body>
      </html>
    `;

    // Create blob and URL
    const blob = new Blob([fullHtml], { type: 'text/html' });
    this.invoiceHtmlBlobUrl = URL.createObjectURL(blob);
    this.invoiceHtmlUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.invoiceHtmlBlobUrl);
  }

  /**
   * Clean up blob URL on component destroy
   */
  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
    if (this.invoiceHtmlBlobUrl) {
      URL.revokeObjectURL(this.invoiceHtmlBlobUrl);
      this.invoiceHtmlBlobUrl = null;
    }
    if (this.previewBlobUrl) {
      URL.revokeObjectURL(this.previewBlobUrl);
      this.previewBlobUrl = null;
    }
  }

  /**
   * Check which tax authority actions are available
   */
  checkTaxAuthorityActions(): void {
    if (!this.invoice) return;

    this.procedureFlags = computeProcedureActionFlags(this.invoice);
    this.canContinue = this.procedureFlags.canContinue;
    this.canFurtherObjection = this.procedureFlags.canFurtherObjection;
    this.canAfterHearingApproval = this.procedureFlags.canAfterHearingApproval;
    this.canResendSecond = this.procedureFlags.canResendSecond;
    this.canCancel = this.procedureFlags.canCancel;

    const isReverseInvoice = +this.invoice.invoice_type === 330;
    const isCancelled =
      +this.invoice.procedure === 3 || this.invoice.is_deleted === 1;
    if (isCancelled) {
      this.canCancel = false;
      this.canFurtherObjection = false;
      this.canAfterHearingApproval = false;
      this.canContinue = false;
      this.canResendSecond = false;
      this.canRequestAllocationNumber = false;
      this.canBulkMerge = false;
      return;
    }

    // Can request allocation number if invoice was created without allocation (allocation_number=0)
    this.canRequestAllocationNumber =
      canRequestAllocationForType(this.invoice.invoice_type) &&
      this.invoice.confirm === 1 &&
      this.invoice.is_deleted === 0 &&
      (this.invoice.allocation_number === 0 || this.invoice.allocation_number == null) &&
      !this.invoice.confirmation_number;

    // Bulk-merge from view: طلبية (100)، إرسالية (200)، مطالبة مالية (332) only
    const docType = +this.invoice.invoice_type;
    const isMergeSourceType = docType === 100 || docType === 200 || docType === 332;
    const currentType
      = this.invoiceTypes.find(t => String(t.id) === String(this.invoice.invoice_type));
    this.canBulkMerge =
      this.invoice.confirm === 1 &&
      this.invoice.is_deleted === 0 &&
      !isReverseInvoice &&
      !this.hasInvoiceReferenceNumber(this.invoice) &&
      !this.isInvoiceMarkedMergedDb(this.invoice) &&
      isMergeSourceType &&
      !!currentType?.can_convert;
  }

  getProcedureLabel(): string {
    return getProcedureLabel(this.invoice?.procedure, this.curentLang);
  }

  getPaymentStatusLabel(): string | null {
    if (!shouldShowPaymentTrackingInUi(this.invoice)) {
      return null;
    }
    return getInvoicePaymentStatusLabel(this.invoice, this.curentLang);
  }

  getPaymentStatusBadgeClass(): string {
    if (!shouldShowPaymentTrackingInUi(this.invoice)) {
      return '';
    }
    return getEffectivePaymentStatusBadgeClass(
      computeEffectivePaymentStatus(this.invoice?.payment_status, this.invoice?.due_date),
    );
  }

  isPaymentTrackingInvoice(): boolean {
    return shouldShowPaymentTrackingInUi(this.invoice);
  }

  canEditPayment(): boolean {
    return (
      !!this.invoice &&
      this.invoice.confirm === 1 &&
      this.invoice.is_deleted !== 1 &&
      isPaymentTrackingInvoiceType(this.invoice.invoice_type)
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (
      target?.closest(
        '.payment-due-popover, .payment-status-popover, .payment-status-badge--editable, .payment-field-trigger, .payment-inline-wrap, .p-calendar, .p-datepicker, .p-datepicker-panel, .p-datepicker-header, .p-datepicker-calendar, .p-datepicker-buttonbar, .p-monthpicker, .p-yearpicker, .p-link',
      )
    ) {
      return;
    }
    this.closeInlinePaymentEditors();
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onWindowScrollOrResize(): void {
    if (this.showDueDateEditor && this.dueDateAnchorEl) {
      this.positionDueDatePopover(this.dueDateAnchorEl);
      this.scheduleDueDatePopoverPosition();
    }
    if (this.showPaymentStatusMenu && this.paymentStatusAnchorEl) {
      this.positionPaymentStatusMenu(this.paymentStatusAnchorEl);
    }
  }

  private positionPaymentStatusMenu(anchor: HTMLElement): void {
    const rect = anchor.getBoundingClientRect();
    const width = InvoicesV2ViewComponent.PAYMENT_STATUS_MENU_WIDTH;
    const height = InvoicesV2ViewComponent.PAYMENT_STATUS_MENU_HEIGHT;
    const margin = 8;
    const gap = 4;
    let left = rect.left;
    if (document.documentElement.dir === 'rtl') {
      left = rect.right - width;
    }
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    let top =
      spaceBelow >= height + gap || spaceBelow >= spaceAbove
        ? rect.bottom + gap
        : rect.top - height - gap;
    top = Math.max(margin, Math.min(top, window.innerHeight - height - margin));
    this.paymentStatusMenuStyle = { top: `${top}px`, left: `${left}px` };
  }

  private positionDueDatePopover(anchor: HTMLElement): void {
    const rect = anchor.getBoundingClientRect();
    const width = InvoicesV2ViewComponent.DUE_DATE_POPOVER_WIDTH;
    const margin = 8;
    const gap = 6;
    let left = rect.left;
    if (document.documentElement.dir === 'rtl') {
      left = rect.right - width;
    }
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
    const popoverEl = this.dueDatePopoverRef?.nativeElement;
    const popoverHeight =
      popoverEl?.offsetHeight || InvoicesV2ViewComponent.DUE_DATE_POPOVER_ESTIMATED_HEIGHT;
    const maxPopoverHeight = window.innerHeight - margin * 2;
    const effectiveHeight = Math.min(popoverHeight, maxPopoverHeight);
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    let top =
      spaceBelow >= effectiveHeight + gap || spaceBelow >= spaceAbove
        ? rect.bottom + gap
        : rect.top - effectiveHeight - gap;
    top = Math.max(margin, Math.min(top, window.innerHeight - effectiveHeight - margin));
    this.dueDatePopoverStyle = {
      top: `${top}px`,
      left: `${left}px`,
      maxHeight: `${maxPopoverHeight}px`,
    };
  }

  private scheduleDueDatePopoverPosition(): void {
    if (!this.dueDateAnchorEl) return;
    const anchor = this.dueDateAnchorEl;
    setTimeout(() => {
      if (this.showDueDateEditor && this.dueDateAnchorEl === anchor) {
        this.positionDueDatePopover(anchor);
      }
    }, 0);
  }

  closeInlinePaymentEditors(): void {
    this.showPaymentStatusMenu = false;
    this.paymentStatusMenuStyle = null;
    this.paymentStatusAnchorEl = null;
    this.showDueDateEditor = false;
    this.dueDatePopoverStyle = null;
    this.dueDateAnchorEl = null;
    this.dueDateEditValue = null;
    this.awaitingDueDateForUnpaid = false;
  }

  togglePaymentStatusMenu(event: Event): void {
    event.stopPropagation();
    if (!this.canEditPayment() || this.isSavingPaymentUpdate) return;
    this.showDueDateEditor = false;
    this.dueDatePopoverStyle = null;
    this.dueDateAnchorEl = null;
    this.dueDateEditValue = null;
    this.awaitingDueDateForUnpaid = false;

    if (this.showPaymentStatusMenu) {
      this.showPaymentStatusMenu = false;
      this.paymentStatusMenuStyle = null;
      this.paymentStatusAnchorEl = null;
      return;
    }

    const anchor = event.currentTarget as HTMLElement;
    this.showPaymentStatusMenu = true;
    this.paymentStatusAnchorEl = anchor;
    anchor.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    this.positionPaymentStatusMenu(anchor);
    setTimeout(() => {
      if (this.showPaymentStatusMenu && this.paymentStatusAnchorEl === anchor) {
        this.positionPaymentStatusMenu(anchor);
      }
    }, 0);
  }

  selectPaymentStatus(status: StoredPaymentStatus, event: Event): void {
    event.stopPropagation();
    if (!this.canEditPayment() || !this.invoice) return;
    this.showPaymentStatusMenu = false;
    this.paymentStatusMenuStyle = null;
    this.paymentStatusAnchorEl = null;

    if (needsDueDateBeforeUnpaid(this.invoice, status)) {
      this.awaitingDueDateForUnpaid = true;
      this.dueDateEditValue = null;
      this.toastr.info(this.translate.instant('invoices_v2.payment.select_due_date_for_unpaid'));
      setTimeout(() => this.openDueDatePopoverFromTrigger(), 0);
      return;
    }

    if (shouldSkipPaymentStatusPatch(this.invoice, status)) {
      return;
    }
    this.patchInvoicePayment(buildPaymentStatusPatchBody(this.invoice, status));
  }

  private openDueDatePopoverFromTrigger(): void {
    const anchor = document.querySelector(
      '.payment-field-trigger--due-date',
    ) as HTMLElement | null;
    if (!anchor) return;
    this.dueDateAnchorEl = anchor;
    this.showDueDateEditor = true;
    anchor.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    this.positionDueDatePopover(anchor);
    this.scheduleDueDatePopoverPosition();
  }

  startDueDateEdit(event: Event): void {
    event.stopPropagation();
    if (!this.canEditPayment() || !this.invoice || this.isSavingPaymentUpdate) return;
    this.showPaymentStatusMenu = false;
    this.paymentStatusMenuStyle = null;
    this.paymentStatusAnchorEl = null;
    this.awaitingDueDateForUnpaid = false;
    this.showDueDateEditor = true;
    this.dueDateEditValue = parseDueDateFromApi(this.invoice.due_date) ?? new Date();
    const anchor = event.currentTarget as HTMLElement;
    this.dueDateAnchorEl = anchor;
    anchor.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    this.positionDueDatePopover(anchor);
    this.scheduleDueDatePopoverPosition();
  }

  cancelDueDateEdit(event?: Event): void {
    event?.stopPropagation();
    this.showDueDateEditor = false;
    this.dueDatePopoverStyle = null;
    this.dueDateAnchorEl = null;
    this.dueDateEditValue = null;
    this.awaitingDueDateForUnpaid = false;
  }

  saveDueDateInline(event?: Event): void {
    event?.stopPropagation();
    if (!this.canEditPayment() || !this.invoice || !this.showDueDateEditor) return;
    const formatted = formatDueDateForApi(this.dueDateEditValue);

    if (this.awaitingDueDateForUnpaid) {
      if (!formatted) {
        this.toastr.warning(this.translate.instant('invoices_v2.payment.due_date_required_for_unpaid'));
        return;
      }
      this.awaitingDueDateForUnpaid = false;
      this.patchInvoicePayment(buildUnpaidWithDueDatePatchBody(this.dueDateEditValue));
      return;
    }

    const current = this.invoice.due_date ? String(this.invoice.due_date).slice(0, 10) : null;
    if (formatted === current) {
      this.cancelDueDateEdit();
      return;
    }
    this.patchInvoicePayment({ due_date: formatted });
  }

  clearDueDateInline(event: Event): void {
    event.stopPropagation();
    if (!this.canEditPayment() || !this.invoice) return;
    if (this.invoice.due_date == null || this.invoice.due_date === '') {
      this.cancelDueDateEdit();
      return;
    }
    this.patchInvoicePayment(buildClearDueDatePatchBody(this.invoice));
  }

  private patchInvoicePayment(body: UpdateInvoicePaymentBody): void {
    if (!this.invoice || this.isSavingPaymentUpdate) return;

    const snapshot = {
      payment_status: this.invoice.payment_status,
      due_date: this.invoice.due_date,
      effective_payment_status: this.invoice.effective_payment_status,
      payment_status_label_ar: this.invoice.payment_status_label_ar,
      payment_status_label_en: this.invoice.payment_status_label_en,
      payment_status_label_he: this.invoice.payment_status_label_he,
      updatedDate: this.invoice.updatedDate,
      updated_by: this.invoice.updated_by,
    };

    applyPaymentFieldsToInvoiceRow(this.invoice, body);
    this.isSavingPaymentUpdate = true;

    this.invoiceV2Service.updateInvoicePayment(this.invoiceId, body).subscribe({
      next: (response) => {
        this.isSavingPaymentUpdate = false;
        if (response?.data) {
          applyPaymentFieldsToInvoiceRow(
            this.invoice,
            body,
            response.data as unknown as Record<string, unknown>,
          );
        }
        this.toastr.success(
          this.getApiSuccessMessage(
            response,
            this.translate.instant('invoices_v2.payment.update_success'),
          ),
        );
        this.closeInlinePaymentEditors();
      },
      error: (error) => {
        this.isSavingPaymentUpdate = false;
        Object.assign(this.invoice, snapshot);
        this.toastr.error(
          this.getApiErrorMessage(error, this.translate.instant('invoices_v2.payment.update_error')),
        );
      },
    });
  }

  openProcedureModal(): void {
    this.showProcedureModal = true;
  }

  closeProcedureModal(): void {
    this.showProcedureModal = false;
  }

  onProcedureActionCompleted(): void {
    this.showProcedureModal = false;
    this.loadInvoice(false);
  }

  /** True if invoice was already merged (source) — backend stores new id in invoice_reference_number */
  private hasInvoiceReferenceNumber(item: any): boolean {
    const ref = item?.invoice_reference_number;
    return ref != null && String(ref).trim() !== '';
  }

  /** From PATCH /invoices-v2/bulk/mark-merged */
  private isInvoiceMarkedMergedDb(item: any): boolean {
    if (!item) return false;
    return item.is_merged === true || item.is_merged === 1 || item.is_merged === '1';
  }

  /**
   * Edit invoice
   */
  editInvoice(): void {
    if (this.invoice.confirm === 0) {
      this.router.navigate(['/dashboard/invoices-v2/edit', this.invoiceId]);
    } else {
      Swal.fire({
        icon: 'warning',
        title:
          this.curentLang === 'ar'
            ? 'لا يمكن التعديل'
            : this.curentLang === 'he'
              ? 'לא ניתן לעריכה'
              : 'Edit not allowed',
        text:
          this.curentLang === 'ar'
            ? 'لا يمكن تعديل فاتورة مؤكدة. يمكنك إنشاء فاتورة عكسية إذا كنت بحاجة للتصحيح.'
            : this.curentLang === 'he'
              ? 'לא ניתן לערוך חשבונית מאושרת. ניתן ליצור חשבונית זיכוי לצורך תיקון.'
              : 'Confirmed invoices cannot be edited. You can create a reverse invoice to make corrections.',
        confirmButtonColor: '#E62E2D',
        confirmButtonText:
          this.curentLang === 'ar' ? 'حسناً' : this.curentLang === 'he' ? 'בסדר' : 'OK',
      });
    }
  }

  /**
   * Delete invoice
   */
  deleteInvoice(): void {
    if (this.invoice.confirm === 0) {
      Swal.fire({
        title:
          this.curentLang === 'ar'
            ? 'تأكيد الحذف'
            : this.curentLang === 'he'
              ? 'אישור מחיקה'
              : 'Confirm Delete',
        text:
          this.curentLang === 'ar'
            ? 'هل أنت متأكد من حذف هذه الفاتورة؟'
            : this.curentLang === 'he'
              ? 'האם אתה בטוח שברצונך למחוק חשבונית זו?'
              : 'Are you sure you want to delete this invoice?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText:
          this.curentLang === 'ar' ? 'حذف' : this.curentLang === 'he' ? 'מחק' : 'Delete',
        cancelButtonText:
          this.curentLang === 'ar' ? 'إلغاء' : this.curentLang === 'he' ? 'ביטול' : 'Cancel',
      }).then((result) => {
        if (result.isConfirmed) {
          this.spinner.show();
          this.invoiceV2Service.deleteInvoice(this.invoiceId).subscribe({
            next: () => {
              this.spinner.hide();
              this.toastr.success(
                this.curentLang === 'ar'
                  ? 'تم حذف الفاتورة بنجاح'
                  : this.curentLang === 'he'
                    ? 'החשבונית נמחקה בהצלחה'
                    : 'Invoice deleted successfully',
              );
              this.router.navigate(['/dashboard/invoices-v2']);
            },
            error: (error) => {
              this.spinner.hide();
              this.toastr.error(this.getApiErrorMessage(error, 'Error deleting invoice'));
            },
          });
        }
      });
    } else {
      this.toastr.warning(
        this.curentLang === 'ar'
          ? 'لا يمكن حذف فاتورة مؤكدة'
          : this.curentLang === 'he'
            ? 'לא ניתן למחוק חשבונית מאושרת'
            : 'Cannot delete confirmed invoice',
      );
    }
  }

  /**
   * Confirm invoice
   */
  confirmInvoice(): void {
    if (this.invoice.confirm === 0) {
      Swal.fire({
        title:
          this.curentLang === 'ar'
            ? 'تأكيد الفاتورة'
            : this.curentLang === 'he'
              ? 'אישור חשבונית'
              : 'Confirm Invoice',
        text:
          this.curentLang === 'ar'
            ? 'هل أنت متأكد من تأكيد هذه الفاتورة؟'
            : this.curentLang === 'he'
              ? 'האם אתה בטוח שברצונך לאשר חשבונית זו?'
              : 'Are you sure you want to confirm this invoice?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#6c757d',
        confirmButtonText:
          this.curentLang === 'ar' ? 'تأكيد' : this.curentLang === 'he' ? 'אשר' : 'Confirm',
        cancelButtonText:
          this.curentLang === 'ar' ? 'إلغاء' : this.curentLang === 'he' ? 'ביטול' : 'Cancel',
      }).then((result) => {
        if (result.isConfirmed) {
          this.spinner.show();
          this.invoiceV2Service.confirmInvoice(this.invoiceId).subscribe({
            next: (response) => {
              this.spinner.hide();
              if (isProcedureCycleConfirmResponse(response as unknown as Record<string, unknown>)) {
                Swal.fire({
                  icon: 'warning',
                  title: this.getApiSuccessMessage(
                    response,
                    this.curentLang === 'ar'
                      ? 'الفاتورة في دورة إجراءات'
                      : this.curentLang === 'he'
                        ? 'החשבונית במחזור הליכים'
                        : 'Invoice is in procedure cycle',
                  ),
                  text: this.translate.instant('invoices_v2.procedure.confirm_405_hint'),
                  confirmButtonColor: '#E62E2D',
                }).then(() => {
                  this.loadInvoice(false);
                  this.showProcedureModal = true;
                });
                return;
              }
              this.toastr.success(
                this.getApiSuccessMessage(response,
                  this.curentLang === 'ar' ? 'تم تأكيد الفاتورة بنجاح' : this.curentLang === 'he' ? 'החשבונית אושרה בהצלחה' : 'Invoice confirmed successfully'
                ),
              );
              // Pass redirectOnError=false: confirm already succeeded, a reload failure
              // should show an error message in place without navigating away.
              this.loadInvoice(false);
            },
            error: (error) => {
              this.spinner.hide();
              this.toastr.error(this.getApiErrorMessage(error, 'Error confirming invoice'));
            },
          });
        }
      });
    }
  }

  /**
   * Generate PDF (original invoice file from standard endpoint)
   */
  generatePdf(): void {
    this.spinner.show();
    this.invoiceV2Service.generatePdf(this.invoiceId, this.getDocumentLang()).subscribe({
      next: (response) => {
        this.spinner.hide();
        this.downloadPdfFromResponse(
          response,
          this.curentLang === 'ar'
            ? 'تم توليد PDF بنجاح'
            : this.curentLang === 'he'
              ? 'PDF נוצר בהצלחה'
              : 'PDF generated successfully',
        );
      },
      error: (error) => {
        this.spinner.hide();
        this.toastr.error(this.getApiErrorMessage(error, 'Error generating PDF'));
      },
    });
  }

  /**
   * Reprint PDF copy (GET …/pdf/reprint) — keeps original print flow separate
   */
  downloadReprintPdf(): void {
    this.spinner.show();
    this.invoiceV2Service.getReprintPdf(this.invoiceId, this.getDocumentLang()).subscribe({
      next: (blob) => {
        this.spinner.hide();
        if (!(blob instanceof Blob) || blob.size === 0) {
          this.toastr.warning(
            this.curentLang === 'ar'
              ? 'الملف فارغ أو غير صالح.'
              : this.curentLang === 'he'
                ? 'הקובץ ריק או לא תקין.'
                : 'Empty or invalid file.',
          );
          return;
        }
        const slug =
          this.invoice?.document_number != null &&
          String(this.invoice.document_number).trim() !== ''
            ? String(this.invoice.document_number).replace(/[^\w\-_.]+/g, '_')
            : `${this.invoiceId}`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${slug}-reprint.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.toastr.success(
          this.curentLang === 'ar'
            ? 'تم تنزيل نسخة الإعادة بنجاح'
            : this.curentLang === 'he'
              ? 'עותק הדפסה חוזרת הורד בהצלחה'
              : 'Reprint PDF downloaded successfully',
        );
      },
      error: (error) => {
        this.spinner.hide();
        this.toastr.error(
          this.getApiErrorMessage(error, 'Error downloading reprint PDF'),
        );
      },
    });
  }

  private extractPdfLinkFromResponse(response: unknown): string | undefined {
    const r = response as { data?: { pdf_link?: string }; pdf_link?: string };
    if (r?.data != null && typeof r.data === 'object') {
      const pl = (r.data as { pdf_link?: string }).pdf_link;
      if (typeof pl === 'string' && pl.trim()) return pl.trim();
    }
    if (typeof r?.pdf_link === 'string' && r.pdf_link.trim()) {
      return r.pdf_link.trim();
    }
    return undefined;
  }

  private downloadPdfFromResponse(response: unknown, successToast: string, updateStoredPdfLink = true): void {
    const pdfLink = this.extractPdfLinkFromResponse(response);
    if (pdfLink && this.isS3PdfLink(pdfLink)) {
      if (updateStoredPdfLink && this.invoice) {
        this.invoice.pdf_link = pdfLink;
      }
      fetch(pdfLink)
        .then((res) => res.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const filename =
            pdfLink.split('/').pop()?.split('?')[0] || `invoice-${this.invoiceId}.pdf`;
          link.download = filename;
          link.click();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => window.open(pdfLink, '_blank'));
      this.toastr.success(successToast);
    } else {
      this.toastr.warning(
        this.curentLang === 'ar'
          ? 'لم يتم الحصول على رابط تحميل PDF صحيح.'
          : this.curentLang === 'he'
            ? 'לא התקבל קישור הורדה תקין.'
            : 'Could not get valid PDF download link.',
      );
    }
  }

  /**
   * True only if url is the backend S3 PDF link (e.g. https://thinvoice.s3.amazonaws.com/files-dev/...).
   * Reject view page URL (localhost, .../view?invoiceId=...) and blob URLs.
   */
  private isS3PdfLink(url: string | null | undefined): boolean {
    if (!url || typeof url !== 'string' || url.startsWith('blob:')) return false;
    if (/invoices-v2\/view|localhost|dashboard.*invoiceId/i.test(url)) return false;
    return /thinvoice\.s3\.amazonaws\.com|\.s3\.(.*\.)?amazonaws\.com/i.test(url) || (url.startsWith('https://') && url.endsWith('.pdf'));
  }

  /**
   * Get PDF URL for sharing: must be the S3 PDF link (e.g. https://thinvoice.s3.amazonaws.com/files-dev/...),
   * never the view page URL (e.g. .../invoices-v2/view?invoiceId=...).
   * Uses invoice.pdf_link only if it is S3; otherwise calls generatePdf and passes URL to callback.
   */
  private getPdfUrlForSharing(callback: (pdfUrl: string) => void): void {
    const existing = this.invoice?.pdf_link;
    if (existing && this.isS3PdfLink(existing)) {
      callback(existing);
      return;
    }
    this.spinner.show();
    this.invoiceV2Service.generatePdf(this.invoiceId, this.getDocumentLang()).subscribe({
      next: (response) => {
        this.spinner.hide();
        const pdfLink = (response && (response as any).data != null ? (response as any).data?.pdf_link : (response as any)?.pdf_link) as string | undefined;
        if (pdfLink && this.isS3PdfLink(pdfLink)) {
          if (this.invoice) this.invoice.pdf_link = pdfLink;
          callback(pdfLink);
        } else {
          this.toastr.error(
            this.curentLang === 'ar' ? 'لم يتم الحصول على رابط PDF الصحيح. الرابط يجب أن يكون رابط ملف PDF من الخادم.' : this.curentLang === 'he' ? 'לא התקבל קישור PDF תקין.' : 'Could not get valid PDF link from server.',
          );
        }
      },
      error: (error) => {
        this.spinner.hide();
        this.toastr.error(this.getApiErrorMessage(error, 'Error generating PDF'));
      },
    });
  }

  /**
   * Share by email. Tries to send via API; if backend reports no/invalid email opens dialog.
   */
  shareByEmail(): void {
    // Pre-check: if the invoice customer object has no email at all, skip the API call
    const customerEmail =
      this.invoice?.customer?.customer_email ||
      this.invoice?.customer_email;
    if (!customerEmail || !String(customerEmail).trim()) {
      this.openEmailDialog();
      return;
    }
    this.doSendInvoiceEmail();
  }

  private openEmailDialog(): void {
    this.emailForm.reset({ email: '', savePermanently: false });
    this.showEmailDialog = true;
  }

  onCancelEmailDialog(): void {
    this.showEmailDialog = false;
    this.emailForm.reset();
  }

  onSendEmailFromDialog(): void {
    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      return;
    }
    const email = this.emailForm.get('email')?.value?.trim();
    const savePermanently = this.emailForm.get('savePermanently')?.value;
    const customerId = this.invoice?.customer_id || this.invoice?.customer?.customer_id;

    this.showEmailDialog = false;

    if (savePermanently && customerId) {
      this.updateCustomerEmailAndSendInvoice(customerId, email);
    } else {
      this.sendInvoiceWithTempEmail(email);
    }
  }

  private updateCustomerEmailAndSendInvoice(customerId: number, email: string): void {
    this.spinner.show();
    this.httpService.getCustomerById(customerId).subscribe({
      next: (res: any) => {
        const customer = res?.data || res;
        const updated = { ...customer, customer_email: email };
        this.httpService.updateCustomersById(customerId, updated).subscribe({
          next: () => {
            this.doSendInvoiceEmail();
          },
          error: (err: any) => {
            this.spinner.hide();
            this.toastr.error(this.getApiErrorMessage(err,
              this.curentLang === 'ar' ? 'فشل تحديث بريد الزبون' : this.curentLang === 'he' ? 'עדכון האימייל נכשל' : 'Failed to update customer email'));
          },
        });
      },
      error: (err: any) => {
        this.spinner.hide();
        this.toastr.error(this.getApiErrorMessage(err,
          this.curentLang === 'ar' ? 'فشل جلب بيانات الزبون' : this.curentLang === 'he' ? 'שליפת פרטי הלקוח נכשלה' : 'Failed to fetch customer data'));
      },
    });
  }

  private sendInvoiceWithTempEmail(email: string): void {
    this.spinner.show();
    this.httpService.sendInvoiceToCustomerEmail(this.invoiceId, email).subscribe({
      next: (response: any) => {
        this.spinner.hide();
        const isSuccess = response?.success === true;
        if (isSuccess) {
          this.toastr.success(
            this.curentLang === 'ar' ? 'تم إرسال الفاتورة بنجاح' : this.curentLang === 'he' ? 'החשבונית נשלחה בהצלחה' : 'Invoice sent successfully');
        } else {
          this.toastr.error(response?.message ||
            (this.curentLang === 'ar' ? 'فشل إرسال الفاتورة' : this.curentLang === 'he' ? 'שליחת החשבונית נכשלה' : 'Failed to send invoice'));
        }
      },
      error: (err: any) => {
        this.spinner.hide();
        this.toastr.error(this.getApiErrorMessage(err,
          this.curentLang === 'ar' ? 'فشل إرسال الفاتورة' : this.curentLang === 'he' ? 'שליחת החשבונית נכשלה' : 'Failed to send invoice'));
      },
    });
  }

  private doSendInvoiceEmail(): void {
    this.spinner.show();
    this.httpService.sendInvoiceToCustomerEmail(this.invoiceId).subscribe({
      next: (response: any) => {
        this.spinner.hide();
        const isSuccess = response?.success === true;
        // success:false on this endpoint always means the customer has no valid email
        if (!isSuccess) {
          this.openEmailDialog();
          return;
        }
        this.toastr.success(response?.message ||
          (this.curentLang === 'ar' ? 'تم إرسال الفاتورة إلى بريد الزبون' : this.curentLang === 'he' ? 'החשבונית נשלחה לאימייל הלקוח' : 'Invoice sent to customer email'));
      },
      error: () => {
        this.spinner.hide();
        // On any HTTP error, fall back to the email dialog
        this.openEmailDialog();
      },
    });
  }

  /**
   * Share by WhatsApp – same as old documents: PDF URL + message
   */
  shareByWhatsApp(): void {
    this.getPdfUrlForSharing((pdfUrl) => {
      if (!this.isS3PdfLink(pdfUrl)) {
        this.toastr.warning(
          this.curentLang === 'ar' ? 'يجب استخدام رابط ملف PDF من الخادم (رابط التحميل) وليس رابط الصفحة.' : this.curentLang === 'he' ? 'יש להשתמש בקישור קובץ ה-PDF מהשרת.' : 'Use the server PDF file link (download link), not the page link.',
        );
        return;
      }
      const docNum = this.invoice?.document_number || this.invoiceId;
      const langName = this.curentLang === 'ar' ? 'Arabic' : this.curentLang === 'he' ? 'Hebrew' : 'English';
      const message = (this.curentLang === 'ar' ? `فاتورة ${docNum} - ${langName} ` : this.curentLang === 'he' ? `חשבונית ${docNum} - ${langName} ` : `Invoice ${docNum} - ${langName} `) + pdfUrl;
      const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      if (waUrl.length > 2000) {
        this.toastr.warning(
          this.curentLang === 'ar' ? 'الرابط طويل جداً. انسخ الرابط وشاركه يدوياً.' : this.curentLang === 'he' ? 'הקישור ארוך מדי. העתק ושתף ידנית.' : 'Link too long. Copy link and share manually.',
        );
        return;
      }
      window.open(waUrl, '_blank', 'noopener,noreferrer');
      this.toastr.success(
        this.curentLang === 'ar' ? 'جاري فتح واتس آب' : this.curentLang === 'he' ? 'נפתח וואטסאפ' : 'Opening WhatsApp',
      );
    });
  }

  /**
   * Copy invoice PDF link to clipboard (حفظ رابط الفاتورة) – same as old documents: copy PDF URL
   */
  copyInvoiceLink(): void {
    this.getPdfUrlForSharing((pdfUrl) => {
      if (!this.isS3PdfLink(pdfUrl)) {
        this.toastr.warning(
          this.curentLang === 'ar' ? 'يتم نسخ رابط ملف PDF من الخادم فقط (مثل: thinvoice.s3...)، وليس رابط الصفحة.' : this.curentLang === 'he' ? 'מועתק רק קישור קובץ ה-PDF מהשרת.' : 'Only the server PDF file link is copied (e.g. thinvoice.s3...), not the page link.',
        );
        return;
      }
      const onSuccess = (): void => {
        this.toastr.success(
          this.curentLang === 'ar' ? 'تم نسخ رابط الفاتورة' : this.curentLang === 'he' ? 'הקישור הועתק' : 'Invoice link copied',
        );
      };
      const onFail = (): void => {
        this.toastr.error(this.curentLang === 'ar' ? 'فشل النسخ' : this.curentLang === 'he' ? 'ההעתקה נכשלה' : 'Copy failed');
      };
      if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard.writeText(pdfUrl).then(onSuccess, () => this.fallbackCopyToClipboard(pdfUrl, onSuccess, onFail));
      } else {
        this.fallbackCopyToClipboard(pdfUrl, onSuccess, onFail);
      }
    });
  }

  private fallbackCopyToClipboard(text: string, onSuccess: () => void, onFail: () => void): void {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    ta.style.top = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    try {
      const ok = document.execCommand('copy');
      if (ok) {
        onSuccess();
      } else {
        onFail();
      }
    } catch (err) {
      onFail();
    }
    document.body.removeChild(ta);
  }

  /**
   * Same rules as `invoices-v2-list`: confirmed, not deleted, not cancelled in authority flow (`procedure === 3`).
   */
  canCreateReverseInvoice(): boolean {
    if (!this.invoice) {
      return false;
    }
    if (this.invoice.confirm !== 1 || this.invoice.is_deleted === 1) {
      return false;
    }
    const proc = this.invoice.procedure;
    if (proc === 3 || proc === '3') {
      return false;
    }
    return true;
  }

  /**
   * Create reverse invoice
   */
  createReverseInvoice(): void {
    Swal.fire({
      title:
        this.curentLang === 'ar'
          ? 'إنشاء مستند عكسية'
          : this.curentLang === 'he'
            ? 'יצירת חשבונית הפוכה'
            : 'Create Reverse Invoice',
      text:
        this.curentLang === 'ar'
          ? 'هل أنت متأكد من إنشاء مستند عكسية لهذه الفاتورة؟'
          : this.curentLang === 'he'
            ? 'האם אתה בטוח שברצונך ליצור חשבונית הפוכה עבור חשבונית זו?'
            : 'Are you sure you want to create a reverse invoice for this invoice?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText:
        this.curentLang === 'ar' ? 'إنشاء' : this.curentLang === 'he' ? 'צור' : 'Create',
      cancelButtonText:
        this.curentLang === 'ar' ? 'إلغاء' : this.curentLang === 'he' ? 'ביטול' : 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.invoiceV2Service.createReverseInvoice(this.invoiceId).subscribe({
          next: (response) => {
            this.spinner.hide();
            this.toastr.success(
              this.getApiSuccessMessage(response,
                this.curentLang === 'ar' ? 'تم إنشاء الفاتورة العكسية بنجاح' : this.curentLang === 'he' ? 'החשבונית ההפוכה נוצרה בהצלחה' : 'Reverse invoice created successfully'
              ),
            );
            this.router.navigate(['/dashboard/invoices-v2']);
          },
          error: (error) => {
            this.spinner.hide();
            this.toastr.error(this.getApiErrorMessage(error, 'Error creating reverse invoice'));
          },
        });
      }
    });
  }

  /**
   * Tax Authority Actions
   */
  cancelInvoice(): void {
    this.executeTaxAction('cancel');
  }

  furtherObjection(): void {
    this.executeTaxAction('furtherObjection');
  }

  afterHearingApproval(): void {
    this.executeTaxAction('afterHearingApproval');
  }

  /**
   * Request allocation number (رقم تخصيص) for an invoice created without one – sends to tax authority
   */
  requestAllocationNumber(): void {
    if (!canRequestAllocationForType(this.invoice?.invoice_type)) {
      return;
    }
    const title =
      this.curentLang === 'ar'
        ? 'طلب رقم تخصيص'
        : this.curentLang === 'he'
          ? 'בקשת מספר הקצאה'
          : 'Request allocation number';
    const text =
      this.curentLang === 'ar'
        ? 'سيتم إرسال الفاتورة لمصلحة الضرائب للحصول على رقم تخصيص. هل تريد المتابعة؟'
        : this.curentLang === 'he'
          ? 'החשבונית תישלח לרשות המסים לקבלת מספר הקצאה. האם להמשיך?'
          : 'The invoice will be sent to the tax authority to obtain an allocation number. Do you want to continue?';
    Swal.fire({
      title,
      text,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText:
        this.curentLang === 'ar' ? 'نعم' : this.curentLang === 'he' ? 'כן' : 'Yes',
      cancelButtonText:
        this.curentLang === 'ar' ? 'إلغاء' : this.curentLang === 'he' ? 'ביטול' : 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.invoiceV2Service.sendInvoiceSecond(this.invoiceId).subscribe({
          next: (response: any) => {
            this.spinner.hide();
            if (response.status === 200) {
              this.toastr.success(this.getApiSuccessMessage(response,
                this.curentLang === 'ar' ? 'تم الحصول على رقم التخصيص بنجاح' : this.curentLang === 'he' ? 'מספר ההקצאה התקבל בהצלחה' : 'Allocation number received successfully'
              ));
              this.loadInvoice();
            } else {
              this.toastr.warning(this.getApiSuccessMessage(response, 'Action completed with warnings'));
            }
          },
          error: (error) => {
            this.spinner.hide();
            this.toastr.error(this.getApiErrorMessage(error, 'Error requesting allocation number'));
          },
        });
      }
    });
  }

  /**
   * Execute tax authority action
   */
  private executeTaxAction(action: string): void {
    const actionLabels: { [key: string]: { ar: string; he: string; en: string } } = {
      cancel: { ar: 'إلغاء الفاتورة', he: 'ביטול חשבונית', en: 'Cancel Invoice' },
      continue: { ar: 'متابعة الفاتورة', he: 'המשך חשבונית', en: 'Continue Invoice' },
      furtherObjection: { ar: 'اعتراض إضافي', he: 'התנגדות נוספת', en: 'Further Objection' },
      afterHearingApproval: {
        ar: 'موافقة بعد الجلسة',
        he: 'אישור לאחר שימוע',
        en: 'After Hearing Approval',
      },
    };

    const label = actionLabels[action];
    const title =
      this.curentLang === 'ar'
        ? label.ar
        : this.curentLang === 'he'
          ? label.he
          : label.en;

    Swal.fire({
      title: title,
      text:
        this.curentLang === 'ar'
          ? 'هل أنت متأكد من تنفيذ هذا الإجراء؟'
          : this.curentLang === 'he'
            ? 'האם אתה בטוח שברצונך לבצע פעולה זו?'
            : 'Are you sure you want to perform this action?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText:
        this.curentLang === 'ar' ? 'نعم' : this.curentLang === 'he' ? 'כן' : 'Yes',
      cancelButtonText:
        this.curentLang === 'ar' ? 'إلغاء' : this.curentLang === 'he' ? 'ביטול' : 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        let actionObservable;

        switch (action) {
          case 'cancel':
            actionObservable = this.invoiceV2Service.cancelInvoice(this.invoiceId);
            break;
          case 'continue':
            actionObservable = this.invoiceV2Service.continueInvoice(this.invoiceId);
            break;
          case 'furtherObjection':
            actionObservable = this.invoiceV2Service.furtherObjection(this.invoiceId);
            break;
          case 'afterHearingApproval':
            actionObservable = this.invoiceV2Service.afterHearingApproval(this.invoiceId);
            break;
          default:
            return;
        }

        actionObservable.subscribe({
          next: (response) => {
            this.spinner.hide();
            if (response.status === 200) {
              this.toastr.success(this.getApiSuccessMessage(response, 'Action completed successfully'));
              this.loadInvoice();
            } else {
              this.toastr.warning(this.getApiSuccessMessage(response, 'Action completed with warnings'));
            }
          },
          error: (error) => {
            this.spinner.hide();
            this.toastr.error(this.getApiErrorMessage(error, 'Error performing action'));
          },
        });
      }
    });
  }

  /**
   * Get invoice type label
   */
  getInvoiceTypeLabel(type: number): string {
    const invoiceType = this.invoiceTypes.find((t) => t.id === type.toString());
    if (!invoiceType) return type.toString();
    if (this.curentLang === 'ar') return invoiceType.name_ar;
    if (this.curentLang === 'he') return invoiceType.name_he;
    return invoiceType.name_en;
  }

  /**
   * True when the document is a receipt (400) or cancelled receipt (430).
   * Used to hide "tax type" and "send to taxes" fields in receipt view.
   */
  isReceipt(): boolean {
    if (!this.invoice) return false;
    const t = +this.invoice.invoice_type;
    return t === 400 || t === 430;
  }

  /**
   * Receipt (type 400) has no items; its total is stored in total_amount (sum of payment methods).
   * All other types use payment_amount_including_vat (sum of item totals).
   */
  getDisplayAmount(invoice: any): number {
    if (!invoice) return 0;
    if (+invoice.invoice_type === 400) {
      return +invoice.total_amount || 0;
    }
    return +invoice.payment_amount_including_vat || 0;
  }

  isForeignCurrencyInvoice(): boolean {
    return isForeignDocument(this.invoice ?? {});
  }

  getInvoiceCurrencyCode(): string {
    return resolveDocumentCurrency(this.invoice ?? {});
  }

  getForeignCurrencyDetails(): ReturnType<typeof resolveForeignCurrencyFromInvoice> {
    return resolveForeignCurrencyFromInvoice(this.invoice ?? {});
  }

  getForeignOriginalTotal(): number {
    const fc = this.getForeignCurrencyDetails();
    return fc?.total_original ?? 0;
  }

  /**
   * Get status label
   */
  getStatusLabel(): string {
    if (!this.invoice) return '';
    if (this.invoice.is_deleted === 1) {
      return this.curentLang === 'ar' ? 'ملغاة' : this.curentLang === 'he' ? 'מבוטלת' : 'Deleted';
    }
    if (this.invoice.confirm === 1) {
      return this.curentLang === 'ar' ? 'مؤكدة' : this.curentLang === 'he' ? 'מאושרת' : 'Confirmed';
    }
    return this.curentLang === 'ar' ? 'المسودّه' : this.curentLang === 'he' ? 'טיוטה' : 'Draft';
  }

  /**
   * Get status CSS class
   */
  getStatusClass(): string {
    if (!this.invoice) return '';
    if (this.invoice.is_deleted === 1) {
      return 'deleted';
    }
    if (this.invoice.confirm === 1) {
      return 'confirmed';
    }
    return 'saved';
  }

  /**
   * Get status icon
   */
  getStatusIcon(): string {
    if (!this.invoice) return 'fa fa-circle';
    if (this.invoice.is_deleted === 1) {
      return 'fa fa-times-circle';
    }
    if (this.invoice.confirm === 1) {
      return 'fa fa-check-circle';
    }
    return 'fa fa-clock-o';
  }

  /**
   * Go back to list (highlight row when returning from a confirmed invoice).
   */
  goBack(): void {
    const listQueryParams =
      (history.state as { listQueryParams?: Record<string, string | number | null> })?.listQueryParams ?? {};
    const queryParams: Record<string, string | number | null> = { ...listQueryParams };

    if (this.invoiceId && this.invoice?.confirm === 1) {
      queryParams['highlightInvoiceId'] = this.invoiceId;
    } else {
      queryParams['highlightInvoiceId'] = null;
    }

    this.router.navigate(['/dashboard/invoices-v2'], { queryParams });
  }

  /**
   * Toggle invoice list panel
   */
  toggleInvoiceList(): void {
    this.showInvoiceList = !this.showInvoiceList;
    if (this.showInvoiceList && this.invoicesList.length === 0) {
      this.loadInvoicesList();
    }
  }

  /**
   * Load invoices list for selection (confirmed invoices only)
   */
  loadInvoicesList(): void {
    this.isLoadingList = true;
    const companyId = this.invoice?.company_id;
    this.invoiceV2Service.getInvoicesForSelection(companyId, 100).subscribe({
      next: (response: any) => {
        const allInvoices = response?.data || response || [];
        // Filter to show only confirmed invoices (confirm === 1) and not deleted
        this.invoicesList = allInvoices.filter((inv: any) => 
          inv.confirm === 1 && inv.is_deleted !== 1
        );
        // Pre-select current invoice if it's confirmed
        if (this.invoiceId && this.invoice?.confirm === 1 && !this.selectedInvoiceIds.includes(this.invoiceId)) {
          this.selectedInvoiceIds = [this.invoiceId];
        }
        this.isLoadingList = false;
      },
      error: () => {
        this.isLoadingList = false;
        this.toastr.error(
          this.curentLang === 'ar'
            ? 'خطأ في تحميل قائمة الفواتير'
            : this.curentLang === 'he'
              ? 'שגיאה בטעינת רשימת החשבוניות'
              : 'Error loading invoices list'
        );
      }
    });
  }

  /**
   * Toggle invoice selection
   */
  toggleInvoiceSelection(invoiceId: number): void {
    const index = this.selectedInvoiceIds.indexOf(invoiceId);
    if (index > -1) {
      this.selectedInvoiceIds.splice(index, 1);
    } else {
      this.selectedInvoiceIds.push(invoiceId);
    }
  }

  /**
   * Check if invoice is selected
   */
  isInvoiceSelected(invoiceId: number): boolean {
    return this.selectedInvoiceIds.includes(invoiceId);
  }

  /**
   * Select/Deselect all invoices
   */
  toggleSelectAll(): void {
    if (this.selectedInvoiceIds.length === this.invoicesList.length) {
      this.selectedInvoiceIds = [];
    } else {
      this.selectedInvoiceIds = this.invoicesList.map(inv => inv.invoice_id);
    }
  }

  /**
   * View selected invoice
   */
  viewSelectedInvoice(invoiceId: number): void {
    if (invoiceId === this.invoiceId) return;

    this.showInvoiceList = false;
    this.router.navigate(['/dashboard/invoices-v2/view'], {
      queryParams: { invoiceId },
      state: { invoiceId },
      replaceUrl: true,
    });
  }

  // ─── Bulk Merge ────────────────────────────────────────────────────────────

  openBulkMergeModal(): void {
    const cid = this.invoice?.customer_id;
    if (cid == null || cid === ('' as any)) {
      this.toastr.warning(
        this.curentLang === 'ar'
          ? 'لا يمكن الدمج بدون عميل مرتبط بهذه الفاتورة'
          : this.curentLang === 'he'
            ? 'לא ניתן למזג ללא לקוח משויך'
            : 'Cannot merge without a customer on this invoice',
      );
      return;
    }
    if (this.hasInvoiceReferenceNumber(this.invoice)) {
      this.toastr.warning(
        this.curentLang === 'ar'
          ? 'هذه الفاتورة مدمجة مسبقاً'
          : this.curentLang === 'he'
            ? 'חשבונית זו כבר מוזגה'
            : 'This invoice was already merged',
      );
      return;
    }
    if (this.isInvoiceMarkedMergedDb(this.invoice)) {
      this.toastr.warning(
        this.curentLang === 'ar'
          ? 'هذه الفاتورة معلّمة كمدمجة مسبقاً'
          : this.curentLang === 'he'
            ? 'חשבונית זו כבר מסומנת כמוזגת'
            : 'This invoice is already marked as merged',
      );
      return;
    }
    this.bulkMergeSelectedIds = [];
    this.bulkMergeInvoicesList = [];
    this.showBulkMergeModal = true;
    this.loadBulkMergeInvoices();
  }

  closeBulkMergeModal(): void {
    this.showBulkMergeModal = false;
    this.bulkMergeSelectedIds = [];
    this.bulkMergeInvoicesList = [];
  }

  loadBulkMergeInvoices(): void {
    this.isLoadingBulkMerge = true;
    const filters: any = {
      customer_id: this.invoice.customer_id,
      invoice_types: [...BULK_MERGE_POPUP_INVOICE_TYPES],
      confirm: 1,
      limit: 100,
      offset: 0,
    };
    this.invoiceV2Service.getFilteredInvoices(filters).subscribe({
      next: (response: any) => {
        const all: any[] = response?.data || [];
        const allowed = new Set<number>(BULK_MERGE_POPUP_INVOICE_TYPES as unknown as number[]);
        this.bulkMergeInvoicesList = all.filter((inv: any) => {
          const id = this.getBulkMergeRowId(inv);
          const typ = +inv.invoice_type;
          return (
            id > 0 &&
            id !== this.invoiceId &&
            inv.is_deleted !== 1 &&
            !this.hasInvoiceReferenceNumber(inv) &&
            !this.isInvoiceMarkedMergedDb(inv) &&
            allowed.has(typ)
          );
        });
        this.isLoadingBulkMerge = false;
      },
      error: () => {
        this.isLoadingBulkMerge = false;
        this.toastr.error(
          this.curentLang === 'ar'
            ? 'خطأ في تحميل الفواتير'
            : this.curentLang === 'he'
              ? 'שגיאה בטעינת חשבוניות'
              : 'Error loading invoices',
        );
      },
    });
  }

  /** API list rows may use invoice_id or id */
  getBulkMergeRowId(inv: any): number {
    const v = inv?.invoice_id ?? inv?.id;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  /** Backend requires all merged invoices to share the same invoice_type */
  isBulkMergeRowSelectable(inv: any): boolean {
    if (!this.invoice) return false;
    return +inv.invoice_type === +this.invoice.invoice_type;
  }

  /** Hide «select for merge» in preview for rows of the other type (200 vs 332) */
  isPreviewBulkMergeSelectable(): boolean {
    if (this.previewInvoiceId == null) return false;
    const inv = this.bulkMergeInvoicesList.find(
      (i) => this.getBulkMergeRowId(i) === this.previewInvoiceId,
    );
    return inv ? this.isBulkMergeRowSelectable(inv) : false;
  }

  toggleBulkMergeSelection(invoiceId: number): void {
    const inv = this.bulkMergeInvoicesList.find((i) => this.getBulkMergeRowId(i) === invoiceId);
    if (inv && !this.isBulkMergeRowSelectable(inv)) {
      this.toastr.info(
        this.curentLang === 'ar'
          ? 'يمكن دمج مستندات من نفس نوع المستند الحالي فقط (طلبية مع طلبية، إرسالية مع إرسالية، أو مطالبة مع مطالبة).'
          : this.curentLang === 'he'
            ? 'ניתן למזג רק מסמכים מאותו סוג כמו המסמך הנוכחי (הזמנה עם הזמנה, תעודת משלוח עם תעודת משלוח, או דרישה עם דרישה).'
            : 'You can only merge documents of the same type as the current one (order with order, delivery with delivery, or financial request with financial request).',
      );
      return;
    }
    const idx = this.bulkMergeSelectedIds.indexOf(invoiceId);
    if (idx > -1) {
      this.bulkMergeSelectedIds.splice(idx, 1);
    } else {
      this.bulkMergeSelectedIds.push(invoiceId);
    }
  }

  isBulkMergeSelected(invoiceId: number): boolean {
    return this.bulkMergeSelectedIds.includes(invoiceId);
  }

  confirmExcludeFromBulkMerge(inv: any): void {
    const id = this.getBulkMergeRowId(inv);
    if (id < 1 || this.bulkMergeExcludingId != null) {
      return;
    }
    const docLabel = inv?.document_number || `#${id}`;
    this.openBulkMergeExcludeConfirm(docLabel, () => this.excludeInvoiceFromBulkMerge(id));
  }

  /** Close preview/focus before Swal so aria-hidden on app-root does not trap focus in a hidden layer. */
  private openBulkMergeExcludeConfirm(docLabel: string, onConfirm: () => void): void {
    if (this.showInvoicePreviewModal) {
      this.closeInvoicePreview();
    }
    (document.activeElement as HTMLElement | null)?.blur?.();
    setTimeout(() => {
      Swal.fire({
        icon: 'warning',
        title: this.translate.instant('invoices_v2.view.bulk_merge_exclude_confirm_title'),
        text: this.translate.instant('invoices_v2.view.bulk_merge_exclude_confirm_text', { doc: docLabel }),
        showCancelButton: true,
        confirmButtonText: this.translate.instant('invoices_v2.view.bulk_merge_exclude_confirm_ok'),
        cancelButtonText: this.translate.instant('invoices_v2.common.cancel'),
        returnFocus: false,
        focusConfirm: true,
      }).then((result) => {
        if (result.isConfirmed) {
          onConfirm();
        }
      });
    }, 0);
  }

  excludeInvoiceFromBulkMerge(invoiceId: number): void {
    if (invoiceId < 1) {
      return;
    }
    this.bulkMergeExcludingId = invoiceId;
    this.invoiceV2Service.markInvoicesMerged([invoiceId], true).subscribe({
      next: () => {
        this.bulkMergeSelectedIds = this.bulkMergeSelectedIds.filter((x) => x !== invoiceId);
        this.bulkMergeInvoicesList = this.bulkMergeInvoicesList.filter(
          (row) => this.getBulkMergeRowId(row) !== invoiceId,
        );
        if (this.previewInvoiceId === invoiceId) {
          this.closeInvoicePreview();
        }
        this.bulkMergeExcludingId = null;
        this.toastr.success(this.translate.instant('invoices_v2.view.bulk_merge_exclude_success'));
      },
      error: () => {
        this.bulkMergeExcludingId = null;
        this.toastr.error(this.translate.instant('invoices_v2.view.bulk_merge_exclude_error'));
      },
    });
  }

  isBulkMergeExcluding(invoiceId: number): boolean {
    return this.bulkMergeExcludingId === invoiceId;
  }

  private showBulkMergeSuccessAndNavigate(response: any, responseNewId: number): void {
    const msg =
      this.curentLang === 'ar'
        ? response.messageAr || response.message
        : this.curentLang === 'he'
          ? response.messageHe || response.message
          : response.messageEn || response.message;
    this.toastr.success(
      msg ||
        (this.curentLang === 'ar'
          ? 'تم الدمج بنجاح'
          : this.curentLang === 'he'
            ? 'המיזוג הצליח'
            : 'Merged successfully'),
    );
    if (Number.isFinite(responseNewId) && responseNewId > 0) {
      this.router.navigate(['/dashboard/invoices-v2/view'], {
        queryParams: { invoiceId: responseNewId },
        state: { invoiceId: responseNewId },
      });
    } else {
      this.router.navigate(['/dashboard/invoices-v2']);
    }
  }

  executeBulkMerge(): void {
    if (this.bulkMergeSelectedIds.length === 0) {
      this.toastr.warning(
        this.curentLang === 'ar'
          ? 'اختر فاتورة واحدة على الأقل للدمج معها'
          : this.curentLang === 'he'
            ? 'בחר לפחות חשבונית אחת למיזוג'
            : 'Select at least one invoice to merge with',
      );
      return;
    }
    for (const sid of this.bulkMergeSelectedIds) {
      const row = this.bulkMergeInvoicesList.find((i) => this.getBulkMergeRowId(i) === sid);
      if (!row || !this.isBulkMergeRowSelectable(row)) {
        this.toastr.warning(
          this.curentLang === 'ar'
            ? 'الاختيار غير صالح: يجب أن تكون كل المستندات من نفس نوع المستند الحالي.'
            : this.curentLang === 'he'
              ? 'בחירה לא חוקית: כל המסמכים חייבים להיות מאותו סוג כמו הנוכחי.'
            : 'Invalid selection: all documents must match the current document type.',
        );
        return;
      }
    }
    const allIds = [this.invoiceId, ...this.bulkMergeSelectedIds];
    Swal.fire({
      title:
        this.curentLang === 'ar'
          ? 'تأكيد الدمج'
          : this.curentLang === 'he'
            ? 'אישור מיזוג'
            : 'Confirm Merge',
      text:
        this.curentLang === 'ar'
          ? `سيتم دمج ${allIds.length} فواتير في فاتورة ضريبية واحدة. هل تريد المتابعة؟`
          : this.curentLang === 'he'
            ? `${allIds.length} חשבוניות יאוחדו לחשבונית מס אחת. האם להמשיך?`
            : `${allIds.length} invoices will be merged into one tax invoice. Continue?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#17a2b8',
      cancelButtonColor: '#6c757d',
      confirmButtonText:
        this.curentLang === 'ar' ? 'دمج' : this.curentLang === 'he' ? 'מזג' : 'Merge',
      cancelButtonText:
        this.curentLang === 'ar' ? 'إلغاء' : this.curentLang === 'he' ? 'ביטול' : 'Cancel',
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.closeBulkMergeModal();
      this.spinner.show();
      this.invoiceV2Service.bulkConvertInvoices(allIds).subscribe({
        next: (response: any) => {
          const responseNewId = +(response?.invoice_id ?? response?.data?.invoice_id);
          const markIds = [
            ...new Set([
              ...allIds,
              ...(Number.isFinite(responseNewId) && responseNewId > 0 ? [responseNewId] : []),
            ]),
          ];
          this.invoiceV2Service.markInvoicesMerged(markIds, true).subscribe({
            next: () => {
              this.spinner.hide();
              this.showBulkMergeSuccessAndNavigate(response, responseNewId);
            },
            error: () => {
              this.spinner.hide();
              this.toastr.warning(
                this.curentLang === 'ar'
                  ? 'تم الدمج، لكن تعذر تحديث حالة «مدمج» على المستندات.'
                  : this.curentLang === 'he'
                    ? 'המיזוג הצליח, אך לא ניתן היה לעדכן את סימון «מוזג».'
                    : 'Merge succeeded, but the merged flag could not be updated.',
              );
              this.showBulkMergeSuccessAndNavigate(response, responseNewId);
            },
          });
        },
        error: (error) => {
          this.spinner.hide();
          this.toastr.error(this.getApiErrorMessage(error, 'Error merging invoices'));
        },
      });
    });
  }

  // ─── Invoice Preview (inside Bulk Merge) ────────────────────────────────────

  openInvoicePreview(invoiceId: number): void {
    const inv = this.bulkMergeInvoicesList.find((i) => this.getBulkMergeRowId(i) === invoiceId);
    this.previewInvoiceId = invoiceId;
    this.previewInvoiceNumber = inv?.document_number || ('#' + invoiceId);
    this.previewInvoiceUrl = null;
    this.isLoadingPreview = true;
    this.showInvoicePreviewModal = true;

    const docLang = this.getDocumentLang(inv);
    this.invoiceV2Service.getInvoiceHtml(invoiceId, docLang).subscribe({
      next: (html: string) => {
        const fullHtml = `<!DOCTYPE html><html lang="${docLang}"
          dir="${docLang === 'ar' || docLang === 'he' ? 'rtl' : 'ltr'}">
          <head><meta charset="UTF-8">
          <style>body{margin:0;padding:20px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background:#fff;color:#1a1a1a;}*{box-sizing:border-box;}</style>
          </head><body>${html}</body></html>`;
        if (this.previewBlobUrl) {
          URL.revokeObjectURL(this.previewBlobUrl);
        }
        const blob = new Blob([fullHtml], { type: 'text/html' });
        this.previewBlobUrl = URL.createObjectURL(blob);
        this.previewInvoiceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.previewBlobUrl);
        this.isLoadingPreview = false;
      },
      error: () => {
        this.isLoadingPreview = false;
        this.toastr.error(
          this.curentLang === 'ar'
            ? 'تعذر تحميل معاينة الفاتورة'
            : this.curentLang === 'he'
              ? 'לא ניתן לטעון תצוגה מקדימה'
              : 'Could not load invoice preview',
        );
        this.showInvoicePreviewModal = false;
      },
    });
  }

  closeInvoicePreview(): void {
    this.showInvoicePreviewModal = false;
    this.previewInvoiceId = null;
    this.previewInvoiceNumber = '';
    this.previewInvoiceUrl = null;
    if (this.previewBlobUrl) {
      URL.revokeObjectURL(this.previewBlobUrl);
      this.previewBlobUrl = null;
    }
  }

  // ─── Download merged PDF ────────────────────────────────────────────────────

  /**
   * Download merged PDF for selected invoices
   */
  downloadMergedPdf(): void {
    if (this.selectedInvoiceIds.length === 0) {
      this.toastr.warning(
        this.curentLang === 'ar'
          ? 'يرجى اختيار فاتورة واحدة على الأقل'
          : this.curentLang === 'he'
            ? 'אנא בחר חשבונית אחת לפחות'
            : 'Please select at least one invoice'
      );
      return;
    }

    this.spinner.show();
    this.invoiceV2Service.exportMultipleInvoicesPdf(this.selectedInvoiceIds).subscribe({
      next: (blob: Blob) => {
        this.spinner.hide();
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoices_${this.selectedInvoiceIds.join('_')}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        this.toastr.success(
          this.curentLang === 'ar'
            ? 'تم تحميل PDF بنجاح'
            : this.curentLang === 'he'
              ? 'PDF הורד בהצלחה'
              : 'PDF downloaded successfully'
        );
      },
      error: (error) => {
        this.spinner.hide();
        this.toastr.error(this.getApiErrorMessage(error, 'Error downloading PDF'));
      }
    });
  }
}

